import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import ProfilePhotoPicker from '../../components/ProfilePhotoPicker';

const CreateSupervisor = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    region: '',
    city_id: '',
    custom_lat: '',
    custom_lng: '',
    custom_location_name: '',
    zone_locales: [],
    notes: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Cities data
  const [cities, setCities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showCustomLocation, setShowCustomLocation] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityDropdownRef = useRef(null);

  const [currentVille, setCurrentVille] = useState('');
  const [currentZoneLocale, setCurrentZoneLocale] = useState('');
  const [currentNote, setCurrentNote] = useState('');

  // Fetch cities and regions on component mount
  useEffect(() => {
    const fetchCitiesAndRegions = async () => {
      try {
        const [citiesResponse, regionsResponse] = await Promise.all([
          api.get('/cities'),
          api.get('/cities/regions')
        ]);
        
        // Handle the API response format { success: true, data: [...] }
        setCities(citiesResponse.data.success ? citiesResponse.data.data : []);
        setRegions(regionsResponse.data.success ? regionsResponse.data.data : []);
      } catch (error) {
        console.error('Error fetching cities and regions:', error);
        // Set empty arrays as fallback
        setCities([]);
        setRegions([]);
      }
    };

    fetchCitiesAndRegions();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleArrayAdd = (field, value, setter) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
    }

    if (!formData.city_id && !showCustomLocation && !formData.custom_location_name) {
      newErrors.city_id = 'Veuillez sélectionner une ville ou utiliser un emplacement personnalisé';
    }

    if (showCustomLocation) {
      if (!formData.custom_location_name.trim()) {
        newErrors.custom_location_name = 'Le nom de l\'emplacement personnalisé est requis';
      }
      if (!formData.custom_lat) {
        newErrors.custom_lat = 'La latitude est requise pour un emplacement personnalisé';
      }
      if (!formData.custom_lng) {
        newErrors.custom_lng = 'La longitude est requise pour un emplacement personnalisé';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle city selection
  const handleCitySelect = (city) => {
    console.log('City selected:', city);
    setFormData(prev => ({
      ...prev,
      city_id: city.id,
      region: city.region
    }));
    setCitySearch(city.name);
    setShowCustomLocation(false);
    setShowCityDropdown(false);
    console.log('Form data updated with city:', city.id, city.name);
  };

  // Handle custom location toggle
  const handleCustomLocationToggle = () => {
    setShowCustomLocation(!showCustomLocation);
    setShowCityDropdown(false);
    if (!showCustomLocation) {
      setFormData(prev => ({
        ...prev,
        city_id: '',
        region: ''
      }));
      setCitySearch('');
      setSelectedRegion('');
    }
  };

  // Handle region filter change
  const handleRegionChange = (e) => {
    const newRegion = e.target.value;
    setSelectedRegion(newRegion);
    
    // If a city is selected but doesn't match the new region filter, clear it
    if (formData.city_id && newRegion && Array.isArray(cities)) {
      const selectedCity = cities.find(city => city.id === formData.city_id);
      if (selectedCity && selectedCity.region !== newRegion) {
        setFormData(prev => ({
          ...prev,
          city_id: '',
          region: ''
        }));
        setCitySearch('');
      }
    }
  };

  // Filter cities based on search and region
  const filteredCities = Array.isArray(cities) ? cities.filter(city => {
    const matchesSearch = city.name.toLowerCase().includes(citySearch.toLowerCase());
    const matchesRegion = !selectedRegion || city.region === selectedRegion;
    return matchesSearch && matchesRegion;
  }) : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      // Remove password_confirmation from the data sent to API
      const { password_confirmation, ...supervisorData } = formData;
      
      const response = await api.post('/supervisors', supervisorData);

      // The photo needs the new id, so it is sent once the prestataire exists.
      // A failure here must not lose the created record.
      const newId = response.data?.supervisor?.id;
      if (photoFile && newId) {
        try {
          const form = new FormData();
          form.append('photo', photoFile, 'photo.jpg');
          await api.post(`/supervisors/${newId}/photo`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
        }
      }

      // If no error is thrown, creation was successful
      setSuccess(true);
      setTimeout(() => {
        navigate('/supervisors');
      }, 2000);
    } catch (error) {
      console.error('Error creating supervisor:', error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'Erreur lors de la création du prestataire' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        
        <div className="relative z-10 flex items-center justify-center min-h-screen p-6">
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-8 text-center max-w-md w-full">
            <FontAwesomeIcon icon={faCheckCircle} className="text-6xl text-green-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Prestataire Créé !</h2>
            <p className="text-gray-600 mb-4">Le prestataire a été créé avec succès.</p>
            <p className="text-sm text-gray-500">Redirection en cours...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">NOUVEAU PRESTATAIRE</h1>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 lg:p-8">
            {errors.general && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                  {errors.general}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Photo — uploaded right after creation, once the id exists */}
              <ProfilePhotoPicker onSelect={setPhotoFile} />

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Nom complet du prestataire"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="email@exemple.com"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Mot de passe *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                        errors.password ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Mot de passe (min. 8 caractères)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                </div>

                <div>
                  <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmer le mot de passe *
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswordConfirmation ? 'text' : 'password'}
                      id="password_confirmation"
                      name="password_confirmation"
                      value={formData.password_confirmation}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                        errors.password_confirmation ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Confirmer le mot de passe"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirmation((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={showPasswordConfirmation ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    >
                      <FontAwesomeIcon icon={showPasswordConfirmation ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {errors.password_confirmation && <p className="mt-1 text-sm text-red-600">{errors.password_confirmation}</p>}
                </div>
              </div>

              {/* Location Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-2">
                    Région
                  </label>
                  <input
                    type="text"
                    id="region"
                    name="region"
                    value={formData.region}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                    placeholder="Région d'affectation"
                    readOnly
                    disabled
                  />
                  <p className="mt-1 text-xs text-gray-500">La région est automatiquement définie lors de la sélection de la ville</p>
                </div>
              </div>

              {/* City Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ville d'affectation
                </label>
                
                {/* City Search Input */}
                <div className="relative mb-2" ref={cityDropdownRef}>
                  <input
                    type="text"
                    value={citySearch}
                    onChange={(e) => {
                      setCitySearch(e.target.value);
                      setShowCityDropdown(e.target.value.length > 0);
                    }}
                    onFocus={() => setShowCityDropdown(citySearch.length > 0)}
                    onBlur={() => {
                      // Delay hiding to allow click on dropdown items
                      setTimeout(() => setShowCityDropdown(false), 150);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setShowCityDropdown(false);
                      }
                    }}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                      showCustomLocation ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    placeholder="Rechercher une ville..."
                    disabled={showCustomLocation}
                  />
                  
                  {/* City Dropdown */}
                  {showCityDropdown && citySearch && !showCustomLocation && filteredCities.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCities.map((city) => (
                        <button
                          key={city.id}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent input blur
                            handleCitySelect(city);
                            setShowCityDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0 cursor-pointer"
                        >
                          <div className="font-medium text-gray-900">{city.name}</div>
                          <div className="text-sm text-gray-500">{city.region}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Region Filter */}
                <div className="mb-4">
                  <select
                    value={selectedRegion}
                    onChange={handleRegionChange}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                      showCustomLocation ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    disabled={showCustomLocation}
                  >
                    <option value="">Toutes les régions</option>
                    {regions.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Custom Location Toggle */}
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={showCustomLocation}
                      onChange={handleCustomLocationToggle}
                      className="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Utiliser un emplacement personnalisé</span>
                  </label>
                </div>

                {/* Custom Location Fields */}
                {showCustomLocation && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label htmlFor="custom_location_name" className="block text-sm font-medium text-gray-700 mb-1">
                        Nom de l'emplacement
                      </label>
                      <input
                        type="text"
                        id="custom_location_name"
                        name="custom_location_name"
                        value={formData.custom_location_name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                        placeholder="Nom personnalisé"
                      />
                    </div>
                    <div>
                      <label htmlFor="custom_lat" className="block text-sm font-medium text-gray-700 mb-1">
                        Latitude
                      </label>
                      <input
                        type="number"
                        step="any"
                        id="custom_lat"
                        name="custom_lat"
                        value={formData.custom_lat}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                        placeholder="ex: 33.5731"
                      />
                    </div>
                    <div>
                      <label htmlFor="custom_lng" className="block text-sm font-medium text-gray-700 mb-1">
                        Longitude
                      </label>
                      <input
                        type="number"
                        step="any"
                        id="custom_lng"
                        name="custom_lng"
                        value={formData.custom_lng}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                        placeholder="ex: -7.5898"
                      />
                    </div>
                  </div>
                )}

                {/* Selected City Display */}
                {formData.city_id && !showCustomLocation && (
                  <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm bg-green-100 text-green-800 break-words">
                      {citySearch} - {formData.region}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, city_id: '', region: '' }));
                        setCitySearch('');
                        setSelectedRegion('');
                        setShowCityDropdown(false);
                      }}
                      className="text-red-600 hover:text-red-800 text-xs sm:text-sm underline whitespace-nowrap"
                    >
                      Changer
                    </button>
                  </div>
                )}

                {/* Error Messages */}
                {errors.city_id && (
                  <p className="mt-2 text-sm text-red-600">{errors.city_id}</p>
                )}
                {errors.custom_location_name && (
                  <p className="mt-2 text-sm text-red-600">{errors.custom_location_name}</p>
                )}
                {errors.custom_lat && (
                  <p className="mt-2 text-sm text-red-600">{errors.custom_lat}</p>
                )}
                {errors.custom_lng && (
                  <p className="mt-2 text-sm text-red-600">{errors.custom_lng}</p>
                )}
              </div>

              {/* Zones Locales */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zones Locales
                </label>
                <div className="flex gap-2 mb-2 flex-wrap sm:flex-nowrap">
                  <input
                    type="text"
                    value={currentZoneLocale}
                    onChange={(e) => setCurrentZoneLocale(e.target.value)}
                    className="flex-1 min-w-0 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                    placeholder="Ajouter une zone locale"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleArrayAdd('zone_locales', currentZoneLocale, setCurrentZoneLocale))}
                  />
                  <button
                    type="button"
                    onClick={() => handleArrayAdd('zone_locales', currentZoneLocale, setCurrentZoneLocale)}
                    className="w-full sm:w-auto flex-shrink-0 px-4 sm:px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                  >
                    Ajouter
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.zone_locales.map((zone, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                    >
                      {zone}
                      <button
                        type="button"
                        onClick={() => handleArrayRemove('zone_locales', index)}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <div className="flex gap-2 mb-2 flex-wrap sm:flex-nowrap">
                  <input
                    type="text"
                    value={currentNote}
                    onChange={(e) => setCurrentNote(e.target.value)}
                    className="flex-1 min-w-0 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                    placeholder="Ajouter une note"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleArrayAdd('notes', currentNote, setCurrentNote))}
                  />
                  <button
                    type="button"
                    onClick={() => handleArrayAdd('notes', currentNote, setCurrentNote)}
                    className="w-full sm:w-auto flex-shrink-0 px-4 sm:px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                  >
                    Ajouter
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.notes.map((note, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800"
                    >
                      {note}
                      <button
                        type="button"
                        onClick={() => handleArrayRemove('notes', index)}
                        className="ml-2 text-yellow-600 hover:text-yellow-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 sm:space-x-0 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/supervisors')}
                  className="w-full sm:w-auto px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {loading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                  <span>{loading ? 'Création...' : 'Créer le Prestataire'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSupervisor;